import { useConversionTrackingContext } from "@circle-react/contexts/ConversionTrackingContext";
import type { PaywallCurrency } from "@circle-react/types";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";
import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { useCallback, useEffect, useRef } from "react";

import { ConversionTrackingEvent } from "@/react/api/conversionTrackingApi";

import { getConversionTrackingTrialDays } from "../../helpers/conversionTrackingPriceHelpers";
import { generateIdempotencyKey } from "../../helpers/generateIdempotencyKey";
interface UseAddPaymentInfoTrackingParams {
  amountDue: number;
  checkoutSessionId?: number;
  currency: Pick<PaywallCurrency, "code" | "smallest_unit">;
  funnelUuid: string;
  getSelectedPrice: () => PaywallPrice;
  isExpressMode: boolean;
  isPreviewReady: boolean;
  isRedirectReturnMount: boolean;
  paywallId: number;
}

// PAY-404 (Slice 1.2) -- fires `add_payment_info` on `event.complete` for a
// payment-method *type* different from the one most recently fired, not on
// `onReady`. Guarding against the last-fired type (not a set of every type
// ever fired) is deliberate: a member can go Card (fires) -> Klarna (fires)
// -> back to Card, and that last switch must fire again too -- an ever-fired
// set would wrongly suppress it.
//
// `!isExpressMode`: express/one-click is the saved-payment-method upsell
// path, which fires no funnel events at all per spec.
//
// Fires unconditionally regardless of whether the member already has a
// payment method on file: a prior "skip if already on file" condition broke
// retargeting/upsell campaigns aimed at existing members, which need
// `add_payment_info` from exactly those members.
//
// `value`/`trialDays` are read live via `getSelectedPrice()`/`amountDue` (not
// cached from mount). `begin_checkout` sends neither the same way: it fires
// before `PaywallCheckoutContextProvider` mounts and falls back to the bare
// nominal amount -- a known, structural asymmetry, not an oversight.
export const useAddPaymentInfoTracking = ({
  amountDue,
  checkoutSessionId,
  currency,
  funnelUuid,
  getSelectedPrice,
  isExpressMode,
  isPreviewReady,
  isRedirectReturnMount,
  paywallId,
}: UseAddPaymentInfoTrackingParams) => {
  const { track } = useConversionTrackingContext();

  // Tracks the most recently fired payment type within this mount. The shared
  // conversion-tracking context separately guards duplicate event calls across
  // remounts within the checkout session.
  const lastFiredPaymentMethodTypeRef = useRef<string | null>(null);
  const pendingPaymentMethodTypeRef = useRef<string | null>(null);

  const trackPaymentInfo = useCallback(
    (paymentType: string) => {
      if (paymentType === lastFiredPaymentMethodTypeRef.current) {
        pendingPaymentMethodTypeRef.current = null;
        return;
      }

      if (!isPreviewReady) {
        pendingPaymentMethodTypeRef.current = paymentType;
        return;
      }

      const selectedPrice = getSelectedPrice();
      const wasAccepted = track({
        eventName: ConversionTrackingEvent.ADD_PAYMENT_INFO,
        funnelUuid,
        checkoutSessionId,
        paywallId,
        paywallPriceId: selectedPrice.id,
        paymentType,
        // See `fireNonce` on `TrackConversionEventParams`.
        fireNonce: generateIdempotencyKey(),
        value: amountDue,
        currency: currency.code,
        currencySmallestUnit: currency.smallest_unit,
        trialDays: getConversionTrackingTrialDays(selectedPrice),
      });
      if (wasAccepted) {
        lastFiredPaymentMethodTypeRef.current = paymentType;
      }
      pendingPaymentMethodTypeRef.current = null;
    },
    [
      amountDue,
      currency,
      funnelUuid,
      getSelectedPrice,
      isPreviewReady,
      checkoutSessionId,
      paywallId,
      track,
    ],
  );

  const trackPaymentInfoChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      if (event.complete && !isExpressMode && !isRedirectReturnMount) {
        trackPaymentInfo(event.value.type);
      }
    },
    [isExpressMode, isRedirectReturnMount, trackPaymentInfo],
  );

  useEffect(() => {
    if (
      !isExpressMode &&
      !isRedirectReturnMount &&
      pendingPaymentMethodTypeRef.current
    ) {
      trackPaymentInfo(pendingPaymentMethodTypeRef.current);
    }
  }, [isExpressMode, isRedirectReturnMount, trackPaymentInfo]);

  return { trackPaymentInfoChange };
};
